import { ref, computed } from 'vue';
import { useStorage } from '@vueuse/core';
import { defineStore } from 'pinia';
import { GetFeatureFlag } from '@/services/FeatureFlags/GetFeatureFlag';
import { FeatureFlagIdEnum } from '@/enums/featureFlagIds';

import i18n from '@/i18n';

import { PostLogout } from '@/services/Users/PostLogout';

import { useClientStore } from '@/stores/client';
import { useUserInfoStore } from '@/stores/userInfo';

export const useNavigationStore = defineStore('navigation', () => {
  const clientStore = useClientStore();
  const userInfoStore = useUserInfoStore();

  const attendanceReportNoticeModalHidden = useStorage('attendance-report-notice-modal-hidden');
  const isEnableOlympusFeatureFlagOn = ref(false);
  const loadImageFailed = ref(false);
  const navbarToggler = ref(false);
  const fintechDashboardUrl = ref();
  const totemLeavesDashboardUrl = ref();
  const totemPayrollDashboardUrl = ref();
  const menuState = ref('left');
  const userMenuDropdown = ref(false);
  const isEnableMultiLingualTranslationFeatureFlagOn = ref(false);

  const parentBaseUrl = computed(() => clientStore.client.parentBaseUrl || '');

  const changePasswordUrl = computed(() => clientStore.client.changePasswordUrl || '');

  const redirectURL = (item, e) => {
    if ((e.ctrlKey && e.button === 0) || item.isThirdPartyLink) {
      window.open(item.url, '_blank');
    } else {
      location.href = item.url;
    }
  };

  const setInitials = (fullName) => {
    if (!fullName) {
      return '';
    }

    let words = fullName.split(' ');
    let firstTwoWords = words.slice(0, 2);
    let initials = firstTwoWords.map((word) => word.charAt(0).toUpperCase()).join('');

    return initials;
  };

  const logout = () => {
    PostLogout().then(() => {
      attendanceReportNoticeModalHidden.value = false;
      window.location.href = `${parentBaseUrl.value}/Logout.aspx`;
    });
  };

  const redirectFintechDashboard = () => {
    window.open(fintechDashboardUrl.value);
  };

  const redirectTotemLeavesDashboard = () => {
    window.open(totemLeavesDashboardUrl.value);
  };

  const redirectTotemPayrollDashboard = () => {
    window.open(totemPayrollDashboardUrl.value);
  };

  const handleChangeMenuState = (state) => {
    userMenuDropdown.value = true;

    switch (state) {
      case 'left':
        menuState.value = 'left';
        break;
      case 'right':
        menuState.value = 'right';
        break;

      default:
        menuState.value = 'left';
        break;
    }
  };

  const setLanguage = async (languageCode) => {
    const employeeId = userInfoStore.userInfo.id;

    if (employeeId && isEnableMultiLingualTranslationFeatureFlagOn.value) {
      try {
        const response = await userInfoStore.updateUserSettings({ language: languageCode });

        if (response?.userSettingsId === 0) {
          await userInfoStore.createUserSettings({ language: languageCode });
        }
      } catch (error) {
        console.error('Error saving language preference:', error);
      }
    }

    localStorage.setItem('_language-code', languageCode);
    i18n.global.locale = languageCode;

    location.reload();
  };

  const getFeatureFlags = async () => {
    try {
      const olympusFlag = await GetFeatureFlag(FeatureFlagIdEnum.enableOlympus);
      if (olympusFlag.keyId === FeatureFlagIdEnum.enableOlympus) {
        isEnableOlympusFeatureFlagOn.value = olympusFlag.keyValue;
      }

      const multiLingualFlag = await GetFeatureFlag(FeatureFlagIdEnum.enableMultiLingualTranslation);
      if (multiLingualFlag.keyId === FeatureFlagIdEnum.enableMultiLingualTranslation) {
        isEnableMultiLingualTranslationFeatureFlagOn.value = multiLingualFlag.keyValue;
      }
    } catch (error) {
      console.error('Error fetching feature flags:', error);
    }
  };

  const timesheetDetails = ref();

  const bannerDetails = ref();

  const sideNavStates = ref({
    loader: false,
    error: false,
  });

  return {
    parentBaseUrl,
    changePasswordUrl,
    setInitials,
    redirectURL,
    loadImageFailed,
    navbarToggler,
    isEnableMultiLingualTranslationFeatureFlagOn,
    logout,
    fintechDashboardUrl,
    redirectFintechDashboard,
    totemLeavesDashboardUrl,
    totemPayrollDashboardUrl,
    redirectTotemLeavesDashboard,
    redirectTotemPayrollDashboard,
    menuState,
    userMenuDropdown,
    handleChangeMenuState,
    setLanguage,
    getFeatureFlags,
    isEnableOlympusFeatureFlagOn,
    timesheetDetails,
    bannerDetails,
    sideNavStates,
  };
});
